'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SubscriptionDetail } from '@/lib/api/subscriptions';
import { DataTable } from '@/components/data-table/data-table';
import { useDataTable } from '@/hooks/use-data-table';
import { useSubscriptionPaymentsTableColumns } from './subscription-payments-table-columns';
import { SubscriptionPaymentsTableSkeleton } from './subscription-payments-table-skeleton';
import { MainTableState } from '@/lib/utils/table-state-utils';
import { DEFAULT_SKELETON_ROWS } from '@/config/data-table';

interface SubscriptionPaymentsTabProps {
  subscription: SubscriptionDetail;
}

export function SubscriptionPaymentsTab({ subscription }: SubscriptionPaymentsTabProps) {
  const { t } = useTranslation('subscriptions');
  const payments = subscription.payments || [];
  // Handle table state changes (for future pagination/sorting if needed)
  const handleTableStateChange = React.useCallback((state: MainTableState) => {
    // TODO: Implement pagination/sorting if needed
    console.log('Table state changed:', state);
  }, []);

  // Handle refresh (for future implementation)
  const handleRefresh = React.useCallback(() => {
    // TODO: Implement refresh of subscription data
    console.log('Refreshing payments data');
  }, []);

  const columns = useSubscriptionPaymentsTableColumns();

  // Use the data table hook
  const { table } = useDataTable({
    data: payments,
    columns,
    total: payments.length,
    config: {
      skeletonRows: DEFAULT_SKELETON_ROWS,
    },
    onTableStateChange: handleTableStateChange,
    getRowId: (row: any) => row.id || row.payment?.id || Math.random().toString(),
  });

  if (payments.length === 0) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="py-8 text-center">
            <div className="text-muted-foreground">{t('noPaymentHistory')}</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Payment History Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('paymentHistory')}</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            table={table}
            isLoading={false}
            skeletonRows={<SubscriptionPaymentsTableSkeleton rows={DEFAULT_SKELETON_ROWS} />}
            onRefresh={handleRefresh}
            showFilters={false}
            showToolbar={false}
            showPagination={false}
          />
        </CardContent>
      </Card>
    </div>
  );
}
